import React from "react";
import { Button, Form, Input, Modal, message, Spin } from 'antd';
import { ajax } from "../utils/request";
import { saveToken } from "../utils/storage";
import { useTranslation } from "react-i18next";

// This component handles the forgot password form and API call
function ForgotPasswordForm({ onClose, onBackToLogin }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const { t } = useTranslation();

  const onFinish = async (values) => {
    try {
      setLoading(true);
      const res = await ajax.post("/user/forgot-password", values);
      if (res.data.code === 0) {
        messageApi.success("A password reset link has been sent to your email.");
        // After sending the link, we can close this modal and go back to login
        onBackToLogin();
      } else {
        messageApi.error(res.data.detail || res.data.msg);
      }
    } catch (err) {
      if (err.response && err.response.data && err.response.data.detail) {
        messageApi.error(err.response.data.detail);
      } else {
        messageApi.error("Failed to send reset link. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const emailRules = [
    { required: true, message: 'Please input your email!' },
    { type: 'email', message: 'The input is not a valid email!' },
  ];

  return (
    <div>
      {contextHolder}
      <Form
        form={form}
        name="forgot_password"
        layout="vertical"
        onFinish={onFinish}
      >
        <Form.Item
          label="Email"
          name="email"
          rules={emailRules}
        >
          <Input placeholder="Enter your email address" />
        </Form.Item>
        <div className="flex flex-row items-center justify-center">
          <Button type="primary" htmlType="submit" loading={loading}>
            Send Reset Link
          </Button>
          <Button onClick={onBackToLogin} style={{ marginLeft: 38 }}>
            Cancel
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default function Login({ isOpen, setOpen, setRegisterOpen, onLoginSuccess }) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = React.useState(false);
  const [isForgotPasswordModalOpen, setForgotPasswordModalOpen] = React.useState(false);

  const handleOk = async (values) => {
    try {
      setLoading(true);
      const res = await ajax.post("/user/login", values);
      const access_token = res?.data?.data?.access_token;
      if (!access_token) throw new Error("No token");
      await saveToken(access_token);
      messageApi.open({ type: "success", content: t("login.login_success"), duration: 3 });
      setOpen(false);
      form.resetFields();
      if (onLoginSuccess) {
        onLoginSuccess();
      }
    } catch (e) {
      messageApi.open({ type: "error", content: t("login.login_failed"), duration: 3 });
    } finally {
      setLoading(false);
    }
  };

  function handleCancel() {
    setOpen(false);
  }

  // Handler to close login modal and open forgot password modal
  const handleForgotPasswordModalOpen = (e) => {
    e.preventDefault();
    setOpen(false);
    setForgotPasswordModalOpen(true);
  };

  // Handler to close forgot password modal and re-open login modal
  const handleBackToLogin = () => {
    setForgotPasswordModalOpen(false);
    setOpen(true);
  };

  return (
    <>
      {contextHolder}
      <Modal
        name="basic"
        title={t("login.login")}
        centered={true}
        open={isOpen}
        footer={null}
        onCancel={handleCancel}
        width={450}
      >
        <div className="mt-[25px]">
          <Form form={form} onFinish={handleOk}>
            <Form.Item
              labelCol={{ span: 5 }}
              label={t("login.username")}
              name="username"
              rules={[
                { required: true, message: t("login.enter_username") },
              ]}
            >
              <Input placeholder={t("login.enter_username")} />
            </Form.Item>

            <Form.Item
              label="Password"
              labelCol={{ span: 5 }}
              name="password"
              rules={[
                { required: true, message: t("login.enter_password") },
              ]}
            >
              <Input.Password placeholder={t("login.enter_password")} />
            </Form.Item>
            <div className="text-right pr-[.5em] mb-[10px]">
              <a
                onClick={handleForgotPasswordModalOpen}
                className="cursor-pointer text-[var(--blue-text)] underline"
              >
                {t("login.forgot")}
              </a>
            </div>

            <div className="login-link pl-[.5em] pb-[10px]">
              <span>{t("login.no_account")}</span>
              <a className="login cursor-pointer text-[var(--blue-text)] pl-[.5em] underline" onClick={() => {
                setOpen(false);
                setRegisterOpen(true);
              }} href="#">{t("login.register")}</a>
            </div>

            <div className="flex flex-row items-center justify-center">
              <Button type="primary" htmlType="submit" loading={loading}>
                {t("login.login")}
              </Button>
              <Button onClick={handleCancel} style={{ marginLeft: 38 }}>
                {t("login.cancel")}
              </Button>
            </div>
          </Form>
        </div>
      </Modal>

      {/* Modal for Forgot Password */}
      <Modal
        name="forgot-password"
        title="Forgot Password"
        centered={true}
        open={isForgotPasswordModalOpen}
        footer={null}
        onCancel={handleBackToLogin}
        width={450}
      >
        <ForgotPasswordForm
          onBackToLogin={handleBackToLogin}
        />
      </Modal>
    </>
  );
}